import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Logo } from '../../../components/layout/Logo/Logo';
import { Button, Icon, Input, PasswordInput } from '../../../components/ui';
import { useAuth } from '../../../app/providers/AuthProvider';
import type { FormEvent } from 'react';

const WIRES = [
  'M-10 130 H140 V270 H300 V430 H420',
  'M60 -10 V90 H250 V210 H420',
  'M-10 500 H90 V370 H220 V620',
  'M340 -10 V150 H420',
  'M150 620 V540 H330 V620',
];
const NODES = [[140, 130], [140, 270], [300, 270], [300, 430], [60, 90], [250, 90], [250, 210], [90, 500], [90, 370], [220, 370], [340, 150], [330, 540]];

/** Conduit lines with a blue pulse of current travelling along them. */
function Wiring() {
  return (
    <svg viewBox="0 0 400 600" preserveAspectRatio="xMidYMid slice" className="absolute inset-0 h-full w-full [mask-image:linear-gradient(to_bottom,transparent_4%,black_22%,black_48%,transparent_72%)]" aria-hidden="true">
      <g fill="none" stroke="#1D2A3F" strokeWidth="2" strokeLinejoin="round">
        {WIRES.map((d) => (
          <path key={d} d={d} />
        ))}
      </g>
      <path d={WIRES[0]} fill="none" stroke="#2F7BF0" strokeWidth="3" strokeLinecap="round" strokeDasharray="46 954" pathLength="1000" className="animate-current" />
      <path d={WIRES[2]} fill="none" stroke="#8BB3FA" strokeWidth="3" strokeLinecap="round" strokeDasharray="30 970" pathLength="1000" className="animate-current [animation-delay:-4s]" />
      {NODES.map(([x, y]) => (
        <circle key={`${x}-${y}`} cx={x} cy={y} r="4.5" fill="#101B2D" stroke="#2F7BF0" strokeWidth="2" />
      ))}
    </svg>
  );
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    if (!phone.trim() || !password) {
      setError('Enter your phone number and password.');
      return;
    }
    setLoading(true);
    try {
      await login(phone.replace(/\s/g, ''), password);
      navigate(location.state?.from?.pathname || '/', { replace: true });
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col lg:flex-row">
      <section className="relative overflow-hidden bg-navy-800 px-6 pb-10 pt-8 text-white lg:flex lg:w-[46%] lg:flex-col lg:justify-between lg:p-12">
        <Wiring />
        <div className="relative">
          <Logo />
        </div>
        <div className="relative mt-10 max-w-md lg:mt-0">
          <h1 className="text-[28px] font-semibold leading-tight text-white lg:text-4xl">
            Quotes, jobs, bills and payments for your wiring business.
          </h1>
          <p className="mt-3 hidden text-white/65 lg:block">
            Send quotations with a material list, track every job on site, and know who still has to pay.
          </p>
        </div>
      </section>

      <section className="flex flex-1 items-start justify-center px-5 py-10 lg:items-center">
        <form onSubmit={submit} noValidate className="w-full max-w-sm">
          <h2 className="text-2xl font-semibold">Log in</h2>
          <p className="mt-1 text-sm text-ink-500">Use the phone number and password your owner gave you.</p>

          <div className="mt-7 flex flex-col gap-4">
            <Input
              label="Phone number"
              leftIcon="phone"
              type="tel"
              inputMode="numeric"
              autoComplete="tel"
              placeholder="10 digit mobile number"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              autoFocus
            />
            <PasswordInput label="Password" value={password} onChange={(e) => setPassword(e.target.value)} />
          </div>

          {error && (
            <div role="alert" className="mt-4 flex items-start gap-2 rounded-xl bg-red-50 px-3.5 py-3 text-sm text-red-700">
              <Icon name="alert" size={18} className="mt-px shrink-0" />
              {error}
            </div>
          )}

          <Button type="submit" size="lg" fullWidth loading={loading} className="mt-6">
            Log in
          </Button>
          <p className="mt-5 text-center text-sm text-ink-400">Forgot your password? Ask the owner to reset it.</p>
        </form>
      </section>
    </div>
  );
}
